import { ConditionalLink } from "components/conditionalLink";
import Card from "components/DesignSystem/Card/Card";
import { BareInput } from "components/DesignSystem/TextInput/TextInput";
import { FileInput, FileType } from "components/FileInput/FileInput";
import { Redirect } from "components/icons/Redirect";
import { LocalFilePreview, Preview } from "components/PreviewModal";
import { FINANCIALS_FOLDER_NAME } from "constants/documents";
import {
  CHECKLIST_ITEM_SECTION,
  CHECKLIST_ITEM_TYPE,
  CLOSING_STATUS,
  DOCUMENT_STATUS,
  finalizeFinancialsOrder,
} from "constants/financialClosing";
import { useFormikContext } from "formik";
import { useConstructInternalLink } from "hooks/useConstructInternalLink";
import { useDownloadFile } from "hooks/useDownloadFile";
import { useModal } from "hooks/useModal";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useCallback } from "react";
import {
  ChecklistItem,
  FinancialClosingDetail,
} from "store/apis/financialClosing";
import { debounce } from "utils/debouncing";
import { ActionItem, CheckListUpdatePayload, getStatus } from "./CheckList";
import RadioGroup from "components/DesignSystem/RadioGroup/RadioGroup";
import classNames from "classnames";
import dayjs from "dayjs";
import { ChecklistItemV0 } from "./FinancialClosingVersion/ChecklistItemV0";

const FinalChecklistReviewPendingLabel = ({
  isFinalizeFinancialsDisabled,
  item,
  onCheckListChange,
  setFieldValue,
  index,
  currentIndex,
  isParentAction,
  lastIndex,
}: {
  isFinalizeFinancialsDisabled: boolean;
  item: ChecklistItem;
  onCheckListChange: (payload: CheckListUpdatePayload) => void;
  setFieldValue: (field: string, value: any) => void;
  index: number;
  currentIndex: number;
  isParentAction?: boolean;
  lastIndex: number;
}) => {
  const previewModal = useModal();

  const getFinalizeFinancialsLabel = useCallback((type: string) => {
    switch (type) {
      case CHECKLIST_ITEM_TYPE.REVIEW_BALANCE_SHEET:
        return "Balance Sheet";
      case CHECKLIST_ITEM_TYPE.REVIEW_INCOME_STATEMENT:
        return "Income Statement";
      case CHECKLIST_ITEM_TYPE.REVIEW_CASHFLOW_STATEMENT:
        return "Cash Flow Statement";
      default:
        return "";
    }
  }, []);

  return (
    <>
      <section>
        {isFinalizeFinancialsDisabled ? (
          <section
            className={classNames(
              "-t-mt-4 t-border-b t-flex t-items-center t-justify-between t-gap-1.5 t-py-5 t-w-full t-border-r-0 t-border-0 t-border-solid t-border-i-neutral-10  t-border-l-surface-transparent",
              {
                "t-border-t": currentIndex === 0,
                "t-border-b-0": currentIndex === lastIndex,
                "t-py-6": isParentAction,
              }
            )}
          >
            <section>
              <span className="t-text-subtext t-text-text-60">
                {getFinalizeFinancialsLabel(item.type)}
              </span>
            </section>
            <section>
              {item?.file_url ? (
                <ConditionalLink
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  to={item?.file_url}
                  target="_blank"
                >
                  <span className="t-text-purple-70 t-text-body-sm t-font-medium t-no-underline hover:t-underline">
                    View in live financials
                  </span>
                </ConditionalLink>
              ) : (
                <span
                  onClick={previewModal.open}
                  className="t-text-purple-70 t-cursor-pointer t-text-body-sm t-font-medium t-no-underline hover:t-underline"
                >
                  View document
                </span>
              )}
            </section>
          </section>
        ) : (
          <section className="t-flex t-items-center t-justify-between t-gap-1.5">
            <section>
              <span className=" t-text-subtext t-text-text-60">
                {getFinalizeFinancialsLabel(item.type)}
              </span>
              {/* {item.type !== CHECKLIST_ITEM_TYPE.REVIEW_CASHFLOW_STATEMENT && (
                <span className="after:t-font-bold after:t-text-red after:t-content-['_*']" />
              )} */}
            </section>
            <section>
              {item?.file_url ? (
                <ConditionalLink
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  to={item?.file_url}
                  target="_blank"
                >
                  <span className="t-text-purple-70 t-text-body-sm t-font-medium t-no-underline hover:t-underline">
                    View in live financials
                  </span>
                </ConditionalLink>
              ) : (
                <span
                  onClick={previewModal.open}
                  className="t-text-purple-70 t-cursor-pointer t-text-body-sm t-font-medium t-no-underline hover:t-underline"
                >
                  View document
                </span>
              )}
            </section>
          </section>
        )}
      </section>
      <Preview
        showModal={previewModal.isOpen}
        closeModal={previewModal.close}
        previewId={item.file_data?.uuid}
      />
    </>
  );
};

const FinancialClosingCheckList = ({
  item,
  index,
  onCheckListChange,
  getReportLinks,
  isFormEditable,
  isFinancialVersionV1,
  status,
  entityId,
  folderPath,
}: {
  item: ChecklistItem;
  index: number;
  onCheckListChange: (arg: CheckListUpdatePayload) => Promise<void>;
  getReportLinks: (type: ChecklistItem["type"]) => string;
  isFormEditable: boolean;
  status: string;
  isFinancialVersionV1: boolean;
  entityId: string;
  folderPath: string[];
}) => {
  const { setFieldValue, errors, values } = useFormikContext<{
    checklist_items: ChecklistItem[];
  }>();

  // Helper function to check if an item has validation errors
  const hasItemError = (itemUuid: string) => {
    if (!errors.checklist_items || !Array.isArray(errors.checklist_items)) {
      return false;
    }

    const itemIndex = values.checklist_items.findIndex(
      (item) => item.uuid === itemUuid
    );
    return itemIndex >= 0 && Boolean(errors.checklist_items[itemIndex]);
  };

  const previewModal = useModal();
  const { downloadFile } = useDownloadFile();

  const getFinalizeFinancialsLabel = useCallback((type: string) => {
    switch (type) {
      case CHECKLIST_ITEM_TYPE.REVIEW_BALANCE_SHEET:
        return "Add Balance Sheet";
      case CHECKLIST_ITEM_TYPE.REVIEW_INCOME_STATEMENT:
        return "Add Income Statement";
      case CHECKLIST_ITEM_TYPE.REVIEW_CASHFLOW_STATEMENT:
        return "Add Cash Flow Statement";
      default:
        return "";
    }
  }, []);

  const showError = hasItemError(item.uuid);

  const handleFileUpload = useCallback(
    async (files: FileType[]) => {
      if (!isFormEditable) return;

      let uploadedFile = files[0];

      if ("uuid" in uploadedFile) {
        await onCheckListChange({
          checklistItemId: item.uuid,
          document_id: uploadedFile.uuid as string,
          document_type: DOCUMENT_STATUS.UPLOAD,
        });
      }

      setFieldValue(
        `checklist_items[${index}].file_data`,
        uploadedFile as File
      );
    },
    [isFormEditable, item.uuid, index, onCheckListChange, setFieldValue]
  );

  return (
    <div
      className={classNames(
        "t-border-r-0 t-border-0 t-border-solid t-border-i-neutral-10",
        {
          "t-border-t t-pt-4":
            index === 0 && status === CLOSING_STATUS.REVIEW_PENDING,
          "t-border-t-0 t-pb-0": index === 2,
        }
      )}
    >
      <Card.Root
        className="t-rounded-lg sm:!t-border-0 sm:!t-px-0"
        variant="primary"
      >
        <div
          className="t-flex t-flex-col t-gap-4 t-py-4 t-w-full"
          id={item.uuid}
        >
          <div className="t-text-text-60 t-text-subtext t-flex t-justify-between t-w-full">
            <span>
              {getFinalizeFinancialsLabel(item.type)}
              <span
                className={classNames({
                  "*:after:t-font-bold after:t-text-red after:t-content-['_*']":
                    item.type !== CHECKLIST_ITEM_TYPE.REVIEW_CASHFLOW_STATEMENT,
                })}
              />
            </span>
          </div>
          {isFinancialVersionV1 ? (
            <div className="t-flex t-flex-col t-gap-1">
              <FileInput
                allowUploadToFolder
                withInkleDocs
                block
                entityId={entityId}
                folderPath={folderPath}
                name={`checklist_items[${index}].file_data`}
                file={
                  item.file_data
                    ? {
                        name: item.file_data.name,
                        file_type:
                          item.file_data.file_type || item.file_data.type,
                        is_previewable: item.file_data.is_previewable,
                        uuid: item.file_data.uuid,
                      }
                    : undefined
                }
                {...(isFormEditable && {
                  onDelete: () => {
                    onCheckListChange({
                      checklistItemId: item.uuid,
                      delete_document: true,
                    });
                    setFieldValue(
                      `checklist_items[${index}].file_data`,
                      undefined
                    );
                  },
                })}
                onFileClick={previewModal.open}
                onDrop={handleFileUpload}
                onDownload={
                  item.file_data?.uuid && item.file_data?.group_id
                    ? downloadFile.bind(null, {
                        file_id: item.file_data.uuid,
                        group_id: item.file_data.group_id,
                      })
                    : undefined
                }
                label="Upload file"
                disabled={!isFormEditable}
                type={showError ? "error" : "default"}
              />
              {showError && (
                <div className="t-text-body-sm-italic t-italic t-text-red">
                  This field is required
                </div>
              )}
            </div>
          ) : (
            <ChecklistItemV0
              item={item}
              index={index}
              onCheckListChange={onCheckListChange}
              getReportLinks={getReportLinks}
              isFormEditable={isFormEditable}
              previewModal={previewModal}
              entityId={entityId}
              folderPath={folderPath}
            />
          )}
        </div>
        {item.file_data?.uuid && (
          <Preview
            showModal={previewModal.isOpen}
            closeModal={previewModal.close}
            previewId={item.file_data?.uuid}
          />
        )}

        {!item.file_data?.uuid && previewModal.isOpen && (
          <LocalFilePreview
            isOpen={previewModal.isOpen}
            close={previewModal.close}
            // @ts-ignore
            file={item?.file_data}
          />
        )}
      </Card.Root>
    </div>
  );
};

export default function FinalizeFinancials({
  closingDetails,
  checklistItems,
  onCheckListChange,
  isFormEditable,
  isFinancialVersionV1,
  entityId,
}: {
  checklistItems: ChecklistItem[];
  closingDetails: FinancialClosingDetail;
  onCheckListChange: (payload: CheckListUpdatePayload) => Promise<void>;
  isFormEditable: boolean;
  isFinancialVersionV1: boolean;
  entityId: string;
}) {
  const { link } = useConstructInternalLink();
  const { isCpa, isCustomer } = useRoleBasedView();
  const { start_date, end_date, accounting_method } = closingDetails;
  const year = dayjs(start_date).year();
  const { setFieldValue, errors, values } = useFormikContext<{
    checklist_items: ChecklistItem[];
  }>();

  // Helper function to check if an item has validation errors
  const hasItemErrorInFinalize = (itemUuid: string) => {
    if (!errors.checklist_items || !Array.isArray(errors.checklist_items)) {
      return false;
    }

    const itemIndex = values.checklist_items.findIndex(
      (item) => item.uuid === itemUuid
    );
    return itemIndex >= 0 && Boolean(errors.checklist_items[itemIndex]);
  };

  const finalizeFinancials = checklistItems.filter(
    ({ section }) => section === CHECKLIST_ITEM_SECTION.FINALIZE_FINANCIALS
  );
  const getIndex = (uuid: string) => {
    return checklistItems.findIndex((item) => item.uuid === uuid);
  };

  const isFinalizeFinancialsDisabled =
    closingDetails?.status === CLOSING_STATUS.COMPLETED ||
    (closingDetails?.status === CLOSING_STATUS.REVIEW_PENDING && isCpa);

  const getReportLinks = (type: string) => {
    const REPORT_LINK = {
      [CHECKLIST_ITEM_TYPE.REVIEW_BALANCE_SHEET]: "/books/live-financials",
      [CHECKLIST_ITEM_TYPE.REVIEW_INCOME_STATEMENT]:
        "/books/live-financials/income-statement",
      [CHECKLIST_ITEM_TYPE.REVIEW_CASHFLOW_STATEMENT]:
        "/books/live-financials/cash-flow-statement",
    };

    return link(REPORT_LINK[type as keyof typeof REPORT_LINK], {
      moreQuery: {
        startDate: start_date,
        endDate: end_date,
        accountingMethod: accounting_method,
      },
    });
  };

  const allowUserToEdit =
    closingDetails?.status === CLOSING_STATUS.COMPLETED ||
    (closingDetails?.status === CLOSING_STATUS.REVIEW_PENDING && !isCpa);

  const filteredFinanalizeItems = finalizeFinancials
    ?.filter((item) =>
      finalizeFinancialsOrder.includes(
        item.type as (typeof finalizeFinancialsOrder)[number]
      )
    )
    ?.filter((item) => item.document_type !== "NONE");

  const lastIndex = (filteredFinanalizeItems?.length || 0) - 1;

  return (
    <>
      {filteredFinanalizeItems
        ?.sort(
          (a, b) =>
            finalizeFinancialsOrder.indexOf(
              a.type as (typeof finalizeFinancialsOrder)[number]
            ) -
            finalizeFinancialsOrder.indexOf(
              b.type as (typeof finalizeFinancialsOrder)[number]
            )
        )
        .map((item, i) => {
          const index = getIndex(item.uuid);

          const showError = hasItemErrorInFinalize(item.uuid);

          return allowUserToEdit ? (
            <section key={item.uuid} className="t-group">
              {!isFinalizeFinancialsDisabled ? (
                <div
                  className={classNames(
                    "t-w-full t-border-r-0 t-border-0 t-border-solid t-border-i-neutral-10 t-border-l-surface-transparent group-last:t-border-b-0 t-border-b",
                    {
                      "t-border-t": i === 0,
                      "last:t-mb-0 t-mb-3": allowUserToEdit,
                    }
                  )}
                >
                  <ActionItem
                    id={item.uuid}
                    disabled={false}
                    key={index}
                    showError={showError}
                    label={
                      <FinalChecklistReviewPendingLabel
                        isFinalizeFinancialsDisabled={
                          isFinalizeFinancialsDisabled
                        }
                        item={item}
                        onCheckListChange={onCheckListChange}
                        setFieldValue={setFieldValue}
                        index={index}
                        currentIndex={i}
                        lastIndex={lastIndex}
                      />
                    }
                    description={item.checklist_item_status}
                    status={getStatus({
                      resolved_by_system: item.resolved_by_system,
                      resolved_by_admin: item.resolved_by_admin,
                    })}
                    onIconClick={debounce(() => {
                      if (!isCustomer) return;
                      onCheckListChange({
                        resolved_by_admin: !item.resolved_by_admin,
                        checklistItemId: item.uuid,
                      });
                      setFieldValue(
                        `checklist_items[${index}].resolved_by_admin`,
                        !item.resolved_by_admin
                      );
                    })}
                    parentType="finalize_financials"
                  />
                </div>
              ) : (
                <FinalChecklistReviewPendingLabel
                  isFinalizeFinancialsDisabled
                  item={item}
                  onCheckListChange={onCheckListChange}
                  setFieldValue={setFieldValue}
                  index={index}
                  currentIndex={i}
                  isParentAction
                  lastIndex={lastIndex}
                />
              )}
            </section>
          ) : (
            <div
              key={item.uuid}
              className="t-mb-3 t-border-solid first:t-border-t first:t-pt-4 t-border-0 t-border-neutral-10 last:t-mb-5"
            >
              <FinancialClosingCheckList
                index={index}
                item={item}
                getReportLinks={getReportLinks}
                onCheckListChange={onCheckListChange}
                isFormEditable={isFormEditable}
                status={closingDetails?.status}
                isFinancialVersionV1={isFinancialVersionV1}
                entityId={entityId}
                folderPath={[String(year), FINANCIALS_FOLDER_NAME]}
              />
            </div>
          );
        })}
    </>
  );
}
